import { AddDialog } from '@/components/AddDialog';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { fontFamilies, s } from '@/theme/tokens';
import { Chip, formatStatusLabel, Inline, resolveStatusChipTone, Stack, Text } from '@/ui';
import { Box } from '@/ui/primitives/Box';
import { Checkbox } from '@/ui/primitives/Checkbox';
import { TextField } from '@/ui/primitives/TextField';
import type { ReleaseStatus } from '@flowqa/shared';
import type { ReactElement } from 'react';

const releaseStatusOrder: ReleaseStatus[] = ['draft', 'planned', 'ready', 'released', 'overdue'];
const releaseStatusOrderMap = new Map<ReleaseStatus, number>(
  releaseStatusOrder.map((status, index) => [status, index]),
);

export type ReleaseStatusPreviewItem = {
  id: string;
  name: string;
  projectId: string;
  projectName: string;
  currentStatus: ReleaseStatus;
  nextStatus: ReleaseStatus;
};

type DeleteOrganisationDialogProps = {
  open: boolean;
  organisationName: string;
  deleteName: string;
  loading?: boolean;
  confirmDisabled?: boolean;
  onDeleteNameChange: (value: string) => void;
  onClose: () => void;
  onConfirm: () => void;
};

export const DeleteOrganisationDialog = ({
  open,
  organisationName,
  deleteName,
  loading = false,
  confirmDisabled = false,
  onDeleteNameChange,
  onClose,
  onConfirm,
}: DeleteOrganisationDialogProps): ReactElement => {
  const expectedOrganisationName = organisationName.trim();
  const organisationNameCodeSx = {
    display: 'inline-flex',
    alignItems: 'center',
    fontFamily: fontFamilies.mono,
    backgroundColor: 'action.hover',
    borderRadius: s('xxs'),
    pl: s('sm'),
    pr: s('xs'),
  } as const;
  return (
    <ConfirmDialog
      open={open}
      title="Delete Organisation?"
      confirmLabel="Delete Organisation"
      confirmColor="error"
      loading={loading}
      confirmDisabled={confirmDisabled}
      onClose={onClose}
      onConfirm={onConfirm}
      description={
        <Stack gap="md">
          <Text role="body" color="textSecondary">
            This action is permanent and cannot be undone.
          </Text>
          <Text role="body" color="textSecondary">
            You will be signed out and will need to create a new organisation.
          </Text>
          <Text role="body" color="textSecondary">
            Enter{' '}
            <Box component="code" sx={organisationNameCodeSx}>
              {expectedOrganisationName}
            </Box>{' '}
            to confirm.
          </Text>
          <TextField
            label="Organisation Name"
            value={deleteName}
            onChange={(event) => {
              onDeleteNameChange(event.target.value);
            }}
            fullWidth
          />
        </Stack>
      }
    />
  );
};

type ReleaseStatusPreviewDialogProps = {
  open: boolean;
  saving?: boolean;
  items: ReleaseStatusPreviewItem[];
  selectedReleaseIds: Set<string>;
  onToggleRelease: (id: string, checked: boolean) => void;
  onClose: () => void;
  onSave: () => void;
};

export const ReleaseStatusPreviewDialog = ({
  open,
  saving = false,
  items,
  selectedReleaseIds,
  onToggleRelease,
  onClose,
  onSave,
}: ReleaseStatusPreviewDialogProps): ReactElement => {
  const StatusCheckbox = Checkbox;
  return (
    <AddDialog
      open={open}
      title="Release Status Changes"
      saving={saving}
      onClose={onClose}
      onSave={onSave}
    >
      {items.length === 0 ? (
        <Text role="body" color="textSecondary">
          No releases will change status.
        </Text>
      ) : (
        <Stack gap="md">
          {Object.values(
            items.reduce<Record<string, ReleaseStatusPreviewItem[] | undefined>>((acc, item) => {
              const list = acc[item.projectId] ?? [];
              list.push(item);
              acc[item.projectId] = list;
              return acc;
            }, {}),
          )
            .filter((group): group is ReleaseStatusPreviewItem[] => Boolean(group?.length))
            .map((group) => {
              const sortedGroup = [...group].sort((left, right) => {
                const leftOrder =
                  releaseStatusOrderMap.get(left.nextStatus) ?? releaseStatusOrderMap.size;
                const rightOrder =
                  releaseStatusOrderMap.get(right.nextStatus) ?? releaseStatusOrderMap.size;
                if (leftOrder !== rightOrder) return leftOrder - rightOrder;
                return left.name.localeCompare(right.name);
              });
              return (
                <Stack key={group[0].projectId} gap="sm">
                  <Text role="body">{group[0].projectName}</Text>
                  {sortedGroup.map((item) => (
                    <Inline
                      key={item.id}
                      gap="md"
                      align="center"
                      justify="space-between"
                      wrap="wrap"
                    >
                      <Text role="body">{item.name}</Text>
                      <Inline gap="md" align="center" wrap="wrap">
                        <Inline gap="sm" align="center">
                          <Chip
                            label={formatStatusLabel(item.currentStatus)}
                            tone={resolveStatusChipTone(item.currentStatus)}
                            transform="uppercase"
                            size="small"
                          />
                          <Text role="caption" color="textSecondary">
                            →
                          </Text>
                          <Chip
                            label={formatStatusLabel(item.nextStatus)}
                            tone={resolveStatusChipTone(item.nextStatus)}
                            transform="uppercase"
                            size="small"
                          />
                        </Inline>
                        <StatusCheckbox
                          size="small"
                          checked={selectedReleaseIds.has(item.id)}
                          onChange={(event) => {
                            onToggleRelease(item.id, event.target.checked);
                          }}
                        />
                      </Inline>
                    </Inline>
                  ))}
                </Stack>
              );
            })}
        </Stack>
      )}
    </AddDialog>
  );
};
